import React, { useMemo, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Mail, Code2, Eye, Smartphone, Monitor } from 'lucide-react';
import SEO from '../components/SEO';
import { TEMPLATES, renderTemplate } from '../lib/emails/templates.js';

/**
 * /dev/email-preview/:kind  (hidden — noindex)
 *
 * Internal-only visual QA surface for the 7 transactional email templates.
 * Renders each template against a deterministic sample payload inside an
 * iframe (the email's own CSS shell needs to be isolated from the SPA's
 * Tailwind reset). Tabs: rendered HTML, plain-text fallback, raw HTML
 * source. Desktop ↔ mobile width toggle.
 *
 * Not linked from the app navigation. The route is only reachable by URL
 * and is excluded from search engines via the SEO noindex flag below.
 */

// ─── Deterministic sample payloads ──────────────────────────────────────
const SAMPLES = {
  rfq_confirmation: {
    contact_name: 'Maya Aldrin',
    reference_code: 'RFQ-2026-A8F3K',
    status_label: 'Submitted',
    response_eta: 'within two business days',
    tracking_url: 'https://giwahs.com/track?ref=RFQ-2026-A8F3K',
    support_email: 'hello@giwahs.com',
    category: 'streetwear',
    product_name: 'Heavyweight French-terry hoodie',
    quantity: 800,
    destination_country: 'United States',
    target_price: 'USD 18 — 22 / unit',
    timeline: '90 days',
  },
  rfq_admin_alert: {
    reference_code: 'RFQ-2026-A8F3K',
    contact_name: 'Maya Aldrin',
    company: 'Northwind Apparel',
    contact_email: 'maya@northwind.example',
    contact_phone: '+1 415 555 0102',
    category: 'streetwear',
    product_name: 'Heavyweight French-terry hoodie',
    quantity: 800,
    materials: 'GOTS organic cotton, 450 GSM brushed back',
    branding: 'Embroidered chest hit, screen-printed back, woven hem label',
    packaging: 'Recycled poly bag + hangtag',
    destination_country: 'United States',
    target_price: 'USD 18 — 22 / unit',
    timeline: '90 days',
    notes: 'Tier 1 brand reference attached separately.',
  },
  contact_ack: {
    name: 'Jonas Berg',
    topic: 'partnership',
    company: 'Atelier Modé',
  },
  contact_admin_alert: {
    name: 'Jonas Berg',
    email: 'jonas@ateliermode.example',
    company: 'Atelier Modé',
    topic: 'partnership',
    message: 'We curate independent denim brands and would like to discuss a joint editorial intelligence pilot for Spring 2026.',
  },
  supplier_application_confirmation: {
    contact_name: 'Priya Rangan',
    factory_name: 'Tirupur Circular Knits',
    country: 'India',
    categories: ['activewear', 'knits'],
    capacity: '180,000 units / month',
    interest_level: 'Verified listing + featured tier',
  },
  supplier_application_admin_alert: {
    factory_name: 'Tirupur Circular Knits',
    country: 'India',
    city: 'Tirupur',
    website: 'https://tirupurcircular.example',
    categories: ['activewear', 'knits'],
    capacity: '180,000 units / month',
    moq: '500 — 3,000 units',
    brand_associations: 'Patagonia (tier 2), Outerknown',
    certifications: 'GOTS, Oeko-Tex Standard 100, Higg FEM 2024',
    contact_name: 'Priya Rangan',
    contact_email: 'priya@tirupurcircular.example',
    contact_phone: '+91 422 555 0114',
    interest_level: 'Featured tier',
    notes: 'Has provided Higg facility scores in the application.',
  },
  signup_welcome: {
    email: 'buyer@example.com',
    created_at: '2026-02-27',
  },
};

const FRIENDLY = {
  rfq_confirmation:                    'RFQ — buyer confirmation',
  rfq_admin_alert:                     'RFQ — internal admin alert',
  contact_ack:                         'Contact — submitter acknowledgement',
  contact_admin_alert:                 'Contact — internal admin alert',
  supplier_application_confirmation:   'Supplier app — applicant confirmation',
  supplier_application_admin_alert:    'Supplier app — internal admin alert',
  signup_welcome:                      'Account — signup welcome',
};

const KINDS = Object.keys(TEMPLATES);

const EmailPreview = () => {
  const { kind: kindParam } = useParams();
  const navigate = useNavigate();
  const kind = kindParam && TEMPLATES[kindParam] ? kindParam : KINDS[0];

  const [view, setView]     = useState('rendered');   // 'rendered' | 'text' | 'source'
  const [width, setWidth]   = useState('desktop');    // 'desktop' | 'mobile'
  const [payload, setPayload] = useState(() => JSON.stringify(SAMPLES[kind] || {}, null, 2));
  const [payloadErr, setPayloadErr] = useState('');

  // Re-render whenever kind changes.
  React.useEffect(() => {
    setPayload(JSON.stringify(SAMPLES[kind] || {}, null, 2));
    setPayloadErr('');
  }, [kind]);

  // Auto-generate a QR data URL for rfq_confirmation previews so the
  // embedded image matches what the live email pipeline will produce.
  React.useEffect(() => {
    if (kind !== 'rfq_confirmation') return;
    let cancelled = false;
    (async () => {
      try {
        const QRCode = (await import('qrcode')).default;
        const sample = SAMPLES.rfq_confirmation;
        const url = sample.tracking_url || `https://giwahs.com/track?ref=${sample.reference_code}`;
        const dataUrl = await QRCode.toDataURL(url, {
          errorCorrectionLevel: 'M', margin: 2, width: 300,
          color: { dark: '#0A0A0A', light: '#FFFFFF' },
        });
        if (cancelled) return;
        setPayload(JSON.stringify({ ...sample, qr_data_url: dataUrl }, null, 2));
      } catch (e) {
        // QR optional; preview still works with the textual link CTA.
        console.warn('[EmailPreview QR] skipped', e);
      }
    })();
    return () => { cancelled = true; };
  }, [kind]);

  const rendered = useMemo(() => {
    let parsed;
    try {
      parsed = payload.trim() ? JSON.parse(payload) : {};
      if (payloadErr) setPayloadErr('');
    } catch (e) {
      setPayloadErr(e.message);
      parsed = SAMPLES[kind] || {};
    }
    return renderTemplate(kind, parsed);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, payload]);

  const iframeStyle = width === 'desktop'
    ? { width: '100%', minHeight: 900 }
    : { width: 390, minHeight: 1000, margin: '0 auto', display: 'block', border: '1px solid #000' };

  return (
    <div data-testid="email-preview-page" className="bg-white">
      <SEO title="Email preview (internal)" description="Internal email template QA." noindex />

      <section className="border-b border-black bg-white">
        <div className="mx-auto max-w-[1440px] px-5 py-10 md:px-8 md:py-12">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <span className="label-overline text-neutral-500">/ Internal · Email QA</span>
              <h1 className="mt-3 font-display tracking-tighter" style={{ fontWeight: 700, fontSize: 'clamp(2rem, 4vw, 3.5rem)', lineHeight: 0.95 }}>
                Email preview.
              </h1>
              <p className="mt-3 max-w-2xl text-sm text-neutral-600">
                Sample-payload renders of every transactional template the platform sends. Edit the payload to test edge cases. Not indexable. Not linked from navigation.
              </p>
            </div>
            <Link to="/" data-testid="email-preview-home-link" className="label-overline underline underline-offset-4">← Back to platform</Link>
          </div>

          {/* Template picker */}
          <div className="mt-8 flex flex-wrap gap-2">
            {KINDS.map(k => (
              <button
                key={k}
                data-testid={`email-preview-tab-${k}`}
                onClick={() => navigate(`/dev/email-preview/${k}`)}
                className={`flex items-center gap-2 border border-black px-3 py-2 text-xs ${
                  k === kind ? 'bg-black text-white' : 'bg-white hover:bg-neutral-50'
                }`}
              >
                <Mail size={12} strokeWidth={1.5} />
                {FRIENDLY[k] || k}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white">
        <div className="mx-auto grid max-w-[1440px] gap-8 px-5 py-10 md:grid-cols-[360px_1fr] md:px-8">
          {/* Payload editor */}
          <aside className="border border-black p-5">
            <div className="flex items-center justify-between">
              <span className="label-overline">Sample payload</span>
              <button
                data-testid="email-preview-reset-payload"
                onClick={() => setPayload(JSON.stringify(SAMPLES[kind] || {}, null, 2))}
                className="text-xs text-neutral-500 underline underline-offset-4 hover:text-black"
              >
                Reset
              </button>
            </div>
            <textarea
              data-testid="email-preview-payload-input"
              spellCheck={false}
              value={payload}
              onChange={(e) => setPayload(e.target.value)}
              className="mt-3 h-[460px] w-full resize-none border border-neutral-300 bg-white p-3 font-mono-data text-[11px] leading-relaxed focus:outline-none focus:ring-1 focus:ring-black"
            />
            {payloadErr && <div className="mt-2 border border-black bg-neutral-50 px-3 py-2 text-xs">{payloadErr}</div>}

            <div className="mt-5 border-t border-neutral-200 pt-4">
              <div className="label-overline">Subject</div>
              <div className="mt-1 font-mono-data text-xs">{rendered.subject}</div>
            </div>
            <div className="mt-4 border-t border-neutral-200 pt-4">
              <div className="label-overline">Sent in mock mode?</div>
              <div className="mt-1 text-xs text-neutral-600">No real email is dispatched by this preview — render only. Mock mode at the dispatcher logs to console; live mode hits the Edge Function.</div>
            </div>
          </aside>

          {/* Right pane: rendered / text / source */}
          <div>
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-neutral-200 pb-3">
              <div className="flex gap-2">
                <button data-testid="email-preview-view-rendered" onClick={() => setView('rendered')} className={`flex items-center gap-2 border border-black px-3 py-1.5 text-xs ${view === 'rendered' ? 'bg-black text-white' : 'bg-white hover:bg-neutral-50'}`}>
                  <Eye size={12} strokeWidth={1.5} /> Rendered
                </button>
                <button data-testid="email-preview-view-text" onClick={() => setView('text')} className={`flex items-center gap-2 border border-black px-3 py-1.5 text-xs ${view === 'text' ? 'bg-black text-white' : 'bg-white hover:bg-neutral-50'}`}>
                  Plain text
                </button>
                <button data-testid="email-preview-view-source" onClick={() => setView('source')} className={`flex items-center gap-2 border border-black px-3 py-1.5 text-xs ${view === 'source' ? 'bg-black text-white' : 'bg-white hover:bg-neutral-50'}`}>
                  <Code2 size={12} strokeWidth={1.5} /> HTML source
                </button>
              </div>
              {view === 'rendered' && (
                <div className="flex gap-2">
                  <button data-testid="email-preview-width-desktop" onClick={() => setWidth('desktop')} className={`flex items-center gap-2 border border-black px-3 py-1.5 text-xs ${width === 'desktop' ? 'bg-black text-white' : 'bg-white hover:bg-neutral-50'}`}>
                    <Monitor size={12} strokeWidth={1.5} /> Desktop
                  </button>
                  <button data-testid="email-preview-width-mobile" onClick={() => setWidth('mobile')} className={`flex items-center gap-2 border border-black px-3 py-1.5 text-xs ${width === 'mobile' ? 'bg-black text-white' : 'bg-white hover:bg-neutral-50'}`}>
                    <Smartphone size={12} strokeWidth={1.5} /> Mobile (390)
                  </button>
                </div>
              )}
            </div>

            <div className="mt-5">
              {view === 'rendered' && (
                <iframe
                  data-testid="email-preview-iframe"
                  title={`Email preview · ${kind}`}
                  srcDoc={rendered.html}
                  sandbox=""
                  style={iframeStyle}
                />
              )}
              {view === 'text' && (
                <pre data-testid="email-preview-text" className="overflow-x-auto border border-neutral-300 bg-neutral-50 p-5 font-mono-data text-xs leading-relaxed">{rendered.text}</pre>
              )}
              {view === 'source' && (
                <pre data-testid="email-preview-source" className="overflow-x-auto border border-neutral-300 bg-neutral-50 p-5 font-mono-data text-[10px] leading-relaxed">{rendered.html}</pre>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EmailPreview;
